import { createContext, ReactNode, useContext } from 'react';
import { useSyncMapPin } from './useMapPin';
import {
  useEnforceLockedPinUrl,
  useLockedMapPinState,
  LockedMapPin,
} from './lockedMapPinState';

// True only on maps where the map pin works (home + progression model). The
// locked pin is persisted globally, so consumers use this to ignore it elsewhere.
const LockedMapPinActiveContext = createContext(false);

type LockedMapPinActiveProviderProps = {
  children: ReactNode;
};

// Marks its subtree as a pin-enabled map and wires up the map-pin URL effects.
export const LockedMapPinActiveProvider = ({
  children,
}: LockedMapPinActiveProviderProps): JSX.Element => {
  useSyncMapPin();
  useEnforceLockedPinUrl();

  return (
    <LockedMapPinActiveContext.Provider value>
      {children}
    </LockedMapPinActiveContext.Provider>
  );
};

export const useIsLockedMapPinActive = (): boolean =>
  useContext(LockedMapPinActiveContext);

/**
 * The current map's locked pin (the default hook for anything that reacts to
 * the pin). The pin is stored globally, but only applies inside
 * `LockedMapPinActiveProvider` (the home and progression maps); anywhere else it
 * reads as unlocked (`lockedPin: null`, `isLocked: false`), so callers don't
 * need their own active check.
 *
 * Read-only; use `useLockMapPinActions` to lock or unlock.
 */
export const useLockedMapPin = (): LockedMapPin => {
  const { lockedPin, goToLockedPin, recenterOnLockedPin } =
    useLockedMapPinState();
  const isActiveHere = useIsLockedMapPinActive();

  const activeLockedPin = isActiveHere ? lockedPin : null;

  return {
    lockedPin: activeLockedPin,
    isLocked: activeLockedPin !== null,
    goToLockedPin,
    recenterOnLockedPin,
  };
};
